import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { apiClient } from "../utils/const";
import { toast } from "react-toastify";
import { useTheme } from "./ThemeContext";

// Helper to avoid NaN values
const safeNumber = (val, fallback = 0) =>
    isNaN(val) || val === null ? fallback : val;

// Remove this line - we'll get schemeType dynamically

const AddWithdrawal = ({ onClose, onSuccess }) => {
    const [schemeType, setSchemeType] = useState(localStorage.getItem("schemeType") || "mandal");
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [error, setError] = useState(null);
    const { dark } = useTheme();
    const [withdrawalAmount, setWithdrawalAmount] = useState("");
    const [withdrawalType, setWithdrawalType] = useState(
        schemeType === "kanchan" ? "Metal" : ""
    );
    const [withdrawalDate, setWithdrawalDate] = useState(
        new Date().toISOString().slice(0, 10)
    );

    // Listen for scheme changes
    useEffect(() => {
        const handleSchemeChange = () => {
            const currentScheme = localStorage.getItem("schemeType") || "mandal";
            setSchemeType(currentScheme);
            setWithdrawalType(currentScheme === "kanchan" ? "Metal" : "");
            setSelectedUser(null);
            setWithdrawalAmount("");
        };

        // Listen for scheme change events
        window.addEventListener("schemeChanged", handleSchemeChange);
        
        // Also check on component mount
        handleSchemeChange();

        return () => {
            window.removeEventListener("schemeChanged", handleSchemeChange);
        };
    }, []);

    // Load users
    useEffect(() => {
        const fetchUsers = async () => {
            setError(null);
            const endpoint =
                schemeType === "kanchan"
                    ? "/kanchan/kanchan_withdrawal_users.php"
                    : "/mandal/mandal_withdrawal_users.php";

            try {
                const res = await apiClient.post(endpoint);
                if (res.data.success && res.data.data?.users) {
                    setUsers(res.data.data.users);
                } else {
                    setError(res.data.message || "Unexpected response format");
                    toast.error(res.data.message || "Failed to load users");
                }
            } catch (err) {
                console.error("Failed to load users", err);
                setError("Failed to fetch users.");
                toast.error("Failed to fetch users.");
            }
        };
        fetchUsers();
    }, [schemeType]);

    const getFullName = (user) =>
        [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");

    const selectStyles = useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                minHeight: 44,
                backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#fff",
                borderColor: state.isFocused ? "var(--bs-primary)" : "var(--bs-border-color)",
                boxShadow: state.isFocused ? "0 0 0 3px rgba(102, 126, 234, 0.15)" : "none",
                color: dark ? "#f1f1f1" : "#212529",
                borderRadius: 12,
                transition: "all 0.2s ease",
            }),
            valueContainer: (provided) => ({
                ...provided,
                padding: "6px 12px",
            }),
            input: (provided) => ({
                ...provided,
                color: dark ? "#f1f1f1" : "#212529",
            }),
            singleValue: (provided) => ({
                ...provided,
                color: dark ? "#f1f1f1" : "#212529",
                fontWeight: 500,
            }),
            placeholder: (provided) => ({
                ...provided,
                color: dark ? "rgba(255,255,255,0.5)" : "#6c757d",
            }),
            dropdownIndicator: (provided, state) => ({
                ...provided,
                color: state.isFocused ? "var(--bs-primary)" : dark ? "rgba(255,255,255,0.6)" : "#6c757d",
                paddingRight: 12,
            }),
            clearIndicator: (provided) => ({
                ...provided,
                paddingRight: 12,
                color: dark ? "rgba(255,255,255,0.6)" : "#6c757d",
            }),
            menu: (provided) => ({
                ...provided,
                backgroundColor: dark ? "#1f1f1f" : "#fff",
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid var(--bs-border-color)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
                marginTop: 8,
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                    ? "var(--bs-primary)"
                    : state.isFocused
                    ? "var(--bs-primary-bg-subtle)"
                    : "transparent",
                color: state.isSelected ? "#fff" : dark ? "#f1f1f1" : "#212529",
                fontWeight: state.isSelected ? 600 : 500,
                padding: "10px 14px",
                transition: "background 0.15s ease",
            }),
            menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999,
            }),
        }),
        []
    );

    const userOptions = users.map((user) => ({
        value: user.id,
        label: `${user.card_no || user.id} ${getFullName(user)} ${user.phone || ""}`.trim(),
        data: user,
    }));

    const handleUserChange = (selected) => {
        if (!selected) {
            setSelectedUser(null);
            return;
        }
        const user = selected.data;
        setSelectedUser(user);

        const defaultAmount = safeNumber(Number(user?.total_paid || 0), 0).toFixed(2);
        setWithdrawalAmount(defaultAmount);
        setWithdrawalType(schemeType === "kanchan" ? "Metal" : "");
        setWithdrawalDate(new Date().toISOString().slice(0, 10));
    };

    const totalGoldGms = safeNumber(selectedUser?.total_gold_gms || 0, 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedUser) {
            toast.error("Please select a user first.");
            return;
        }

        const endpoint =
            schemeType === "mandal"
                ? "/mandal/create_mandal_withdrawal.php"
                : "/kanchan/create_kanchan_withdrawal.php";

        const payload = {
            user_id: selectedUser.id,
            card_no: selectedUser.card_no,
            withdrawal_amount: parseFloat(withdrawalAmount || 0),
            withdrawal_type: schemeType === "kanchan" ? "Metal" : withdrawalType,
            withdrawal_date: withdrawalDate,
        };

        if (schemeType === "kanchan") {
            payload.scheme_id = selectedUser.scheme_id;
            payload.withdrawal_in_gms = selectedUser.total_gold_gms || 0;
        }
        try {
            const res = await apiClient.post(endpoint, payload);
            const data = res.data;

            if (data.message === 'radha') {
                toast.success( "Withdrawal successful ✅");

                setSelectedUser(null);
                setWithdrawalAmount("");
                setWithdrawalType(schemeType === "kanchan" ? "Metal" : "");
                setWithdrawalDate(new Date().toISOString().slice(0, 10));

                onSuccess?.();
                onClose?.();
            } else {
                toast.error(data.message || "Withdrawal failed ❌");
            }
        } catch (err) {
            toast.error("Error saving withdrawal ❌", err);
        }
    };

    return (
        <div className="page-wrapper">
            <div className="page-content py-2">
                <div className="container-xxl px-2">
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow-sm mb-2">
                                <div className="card-header add-user-bg text-white fw-bold fs-4 px-3 py-2">
                                    {schemeType === "kanchan"
                                        ? "Withdraw Gold"
                                        : "Withdraw Money"}
                                </div>

                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="mb-2">Select Member</label>
                                            <Select
                                                options={userOptions}
                                                onChange={handleUserChange}
                                                placeholder="Search Member..."
                                                isClearable
                                                styles={selectStyles}
                                                className="react-select-member"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                                                formatOptionLabel={(option) => {
                                                    const u = option.data;
                                                    const name = getFullName(u);
                                                    return (
                                                        <div style={{ whiteSpace: "nowrap" }}>
                                                            Card No: {u.card_no || u.id || "-"}, Name: {name || "-"},
                                                            {" "}
                                                            Phone: {u.phone || "-"}
                                                        </div>
                                                    );
                                                }}
                                                filterOption={(candidate, rawInput) => {
                                                    const input = String(rawInput || "").trim().toLowerCase();
                                                    if (!input) return true;
                                                    const u = candidate?.data || {};
                                                    const idStr = String(u.id ?? candidate.value ?? "").toLowerCase();
                                                    const cardNoStr = String(u.card_no || "").toLowerCase();
                                                    const nameStr = String(getFullName(u) || "").toLowerCase();
                                                    const phoneStr = String(u.phone || "").toLowerCase();
                                                    const labelStr = String(candidate.label || "").toLowerCase();
                                                    return (
                                                        idStr.includes(input) ||
                                                        cardNoStr.includes(input) ||
                                                        nameStr.includes(input) ||
                                                        phoneStr.includes(input) ||
                                                        labelStr.includes(input)
                                                    );
                                                }}
                                                noOptionsMessage={() => "No matches"}
                                                value={
                                                    selectedUser
                                                        ? {
                                                              value: selectedUser.id,
                                                              label: `${selectedUser.card_no || selectedUser.id} ${getFullName(selectedUser)} ${selectedUser.phone || ""}`.trim(),
                                                              data: selectedUser,
                                                          }
                                                        : null
                                                }
                                            />
                                        </div>
                                    </div>

                                    <form className="mt-3" onSubmit={handleSubmit}>
                                        {schemeType === "kanchan" && (
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Total Gold (gms)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={totalGoldGms}
                                                        readOnly
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                {schemeType === "kanchan"
                                                    ? "Total Amount (₹)"
                                                    : "Withdrawal Amount (₹)"}
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={withdrawalAmount}
                                                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <label>Withdrawal Type</label>
                                                {schemeType === "kanchan" ? (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value="Metal"
                                                        readOnly
                                                    />
                                                ) : (
                                                    <select
                                                        className="form-select"
                                                        value={withdrawalType}
                                                        onChange={(e) => setWithdrawalType(e.target.value)}
                                                        required
                                                    >
                                                        <option value="">-- Select --</option>
                                                        <option value="Cash">Cash</option>
                                                        <option value="UPI">UPI</option>
                                                        <option value="Bank Transfer">Bank Transfer</option>
                                                        <option value="Net Banking">Net Banking</option>
                                                    </select>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <label>Withdrawal Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={withdrawalDate}
                                                    onChange={(e) => setWithdrawalDate(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setSelectedUser(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="btn add-user-btn"
                                                        disabled={!selectedUser}
                                                    >
                                                        Save Withdrawal
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .add-user-bg {
                    background-color: #4B2C3B !important;
                }
                .add-user-btn {
                    background-color: #4B2C3B !important;
                    color: #fff !important;
                    border: none;
                    transition: background 0.2s;
                }
                .add-user-btn:hover {
                    background-color: #D3AF37 !important;
                    color: #fff !important;
                }
            `}</style>
        </div>
    );
};

export default AddWithdrawal;
